import { msg } from "@lingui/core/macro";
import { createEffect, createSignal, type JSX, onCleanup, Show } from "solid-js";
import { Dialog, DialogDescription, DialogTitle } from "./dialog";
import { Button } from "./button";
import { useI18n } from "@/providers/i18n-provider";
import { Loader2 } from "lucide-solid";
import { cn } from "@mealplan/shared";

interface ConfirmDialogProps {
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: "default" | "destructive";
  onConfirm: () => void;
  onCancel?: () => void;
  renderTrigger?: (open: () => void) => JSX.Element;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  isLoading?: boolean;
}

/**
 * Confirmation dialog built on Kobalte's Dialog primitive.
 * Uses CSS transitions for smooth entrance and exit animations.
 * Supports Enter key confirmation and displays keyboard shortcut helper kbd badges.
 */
export function ConfirmDialog(props: ConfirmDialogProps) {
  const { t } = useI18n();
  const [internalOpen, setInternalOpen] = createSignal(false);

  const isOpen = () => props.open ?? internalOpen();
  const setIsOpen = (val: boolean) => {
    if (props.onOpenChange) {
      props.onOpenChange(val);
    } else {
      setInternalOpen(val);
    }
  };

  const handleConfirm = () => {
    props.onConfirm();
    // Only close automatically if we are not in controlled mode.
    // If props.open is provided, the parent should handle closing the dialog.
    if (props.open === undefined) {
      setIsOpen(false);
    }
  };

  createEffect(() => {
    if (!isOpen()) {
      return;
    }

    const handleDialogKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleConfirm();
      }
    };

    globalThis.addEventListener("keydown", handleDialogKeyDown);
    onCleanup(() => {
      globalThis.removeEventListener("keydown", handleDialogKeyDown);
    });
  });

  return (
    <>
      {props.renderTrigger?.(() => {
        setIsOpen(true);
      })}
      <Dialog
        role="alertdialog"
        open={isOpen()}
        onOpenChange={setIsOpen}
        contentClass="max-w-[400px] p-6"
      >
        <DialogTitle class="text-xl font-bold mb-2">{props.title}</DialogTitle>
        <DialogDescription class="text-sm text-muted-foreground mb-6">
          {props.description}
        </DialogDescription>
        <div class="flex justify-end gap-3 mt-6">
          <Button
            variant="ghost"
            class="h-11 px-5 rounded-xl font-bold flex items-center gap-1.5"
            onClick={() => {
              props.onCancel?.();
              setIsOpen(false);
            }}
          >
            <kbd class="h-4.5 px-1 inline-flex items-center justify-center rounded border border-border/40 bg-muted/40 font-mono text-[9px] font-bold text-muted-foreground/80 shadow-xs select-none">
              Esc
            </kbd>
            <span>{props.cancelLabel ?? t(msg`Cancel`)}</span>
          </Button>
          <Button
            variant={props.variant === "destructive" ? "destructive" : "default"}
            class="h-11 px-6 rounded-xl font-bold min-w-[120px] flex items-center gap-1.5"
            onClick={handleConfirm}
            disabled={props.isLoading}
          >
            <Show
              when={props.isLoading}
              fallback={
                <>
                  <kbd
                    class={cn(
                      "h-4.5 px-1 inline-flex items-center justify-center rounded font-mono text-[9px] font-bold shadow-xs select-none",
                      props.variant === "destructive"
                        ? "border-red-400/30 bg-red-950/20 text-white/90"
                        : "border-primary-foreground/20 bg-primary-foreground/10 text-primary-foreground",
                    )}
                  >
                    {/* eslint-disable lingui/no-unlocalized-strings -- TODO(i18n) */}↵ Enter
                    {/* eslint-enable lingui/no-unlocalized-strings */}
                  </kbd>
                  <span>{props.confirmLabel ?? t(msg`Confirm`)}</span>
                </>
              }
            >
              <Loader2 class="h-4 w-4 animate-spin mr-2" />
              {t(msg`Working...`)}
            </Show>
          </Button>
        </div>
      </Dialog>
    </>
  );
}
